import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const ShopFilters = ({ 
  isOpen = false, 
  onClose = () => {}, 
  onFiltersChange = () => {},
  initialFilters = {}
}) => {
  const [filters, setFilters] = useState({
    services: [],
    brands: [],
    minRating: 0,
    openNow: false,
    partnerOnly: false,
    hasParking: false,
    wheelchairAccessible: false,
    ...initialFilters
  });

  const serviceOptions = [
    { value: 'sales', label: 'Bike Sales' },
    { value: 'repair', label: 'Repair Services' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'wheel-building', label: 'Wheel Building' },
    { value: 'suspension', label: 'Suspension Service' },
    { value: 'fitting', label: 'Bike Fitting' },
    { value: 'rental', label: 'Bike Rental' },
    { value: 'accessories', label: 'Accessories' }
  ];

  const brandOptions = [
    { value: 'trek', label: 'Trek' },
    { value: 'specialized', label: 'Specialized' },
    { value: 'giant', label: 'Giant' },
    { value: 'cannondale', label: 'Cannondale' },
    { value: 'scott', label: 'Scott' },
    { value: 'bianchi', label: 'Bianchi' },
    { value: 'cervelo', label: 'Cervélo' },
    { value: 'pinarello', label: 'Pinarello' }
  ];

  const ratingOptions = [
    { value: 0, label: 'Any Rating' },
    { value: 3, label: '3+ Stars' },
    { value: 4, label: '4+ Stars' },
    { value: 4.5, label: '4.5+ Stars' }
  ];

  const handleServiceChange = (service, checked) => {
    setFilters(prev => ({
      ...prev,
      services: checked 
        ? [...prev?.services, service]
        : prev?.services?.filter(s => s !== service)
    }));
  };

  const handleBrandChange = (brand, checked) => {
    setFilters(prev => ({
      ...prev,
      brands: checked 
        ? [...prev?.brands, brand]
        : prev?.brands?.filter(b => b !== brand)
    }));
  };

  const handleApplyFilters = () => {
    onFiltersChange(filters);
    onClose();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      services: [],
      brands: [],
      minRating: 0,
      openNow: false,
      partnerOnly: false,
      hasParking: false,
      wheelchairAccessible: false
    };
    setFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.services?.length > 0) count++;
    if (filters?.brands?.length > 0) count++;
    if (filters?.minRating > 0) count++;
    if (filters?.openNow) count++;
    if (filters?.partnerOnly) count++;
    if (filters?.hasParking) count++;
    if (filters?.wheelchairAccessible) count++;
    return count;
  };

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Filter Shops</h3>
        <button
          onClick={onClose}
          className="p-2 hover:bg-muted rounded-full transition-colors interactive-scale lg:hidden"
        >
          <Icon name="X" size={20} />
        </button>
      </div>

      {/* Quick Filters */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Quick Filters
        </label>
        <div className="space-y-2">
          <Checkbox
            label="Open Now"
            checked={filters?.openNow}
            onChange={(e) => setFilters(prev => ({ ...prev, openNow: e?.target?.checked }))}
          />
          <Checkbox
            label="Partner Shops Only"
            checked={filters?.partnerOnly}
            onChange={(e) => setFilters(prev => ({ ...prev, partnerOnly: e?.target?.checked }))}
          />
          <Checkbox
            label="Has Parking"
            checked={filters?.hasParking}
            onChange={(e) => setFilters(prev => ({ ...prev, hasParking: e?.target?.checked }))}
          />
          <Checkbox
            label="Wheelchair Accessible"
            checked={filters?.wheelchairAccessible}
            onChange={(e) => setFilters(prev => ({ ...prev, wheelchairAccessible: e?.target?.checked }))}
          />
        </div>
      </div>

      {/* Rating Filter */}
      <div>
        <Select
          label="Minimum Rating"
          options={ratingOptions}
          value={filters?.minRating}
          onChange={(value) => setFilters(prev => ({ ...prev, minRating: parseFloat(value) }))}
        />
      </div>

      {/* Services */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Services Offered
        </label>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {serviceOptions?.map((service) => (
            <Checkbox
              key={service?.value}
              label={service?.label}
              checked={filters?.services?.includes(service?.value)}
              onChange={(e) => handleServiceChange(service?.value, e?.target?.checked)}
            />
          ))}
        </div>
      </div>

      {/* Brands */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Brands Carried
        </label>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {brandOptions?.map((brand) => (
            <Checkbox
              key={brand?.value}
              label={brand?.label}
              checked={filters?.brands?.includes(brand?.value)}
              onChange={(e) => handleBrandChange(brand?.value, e?.target?.checked)}
            />
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-3 pt-4 border-t border-border">
        <Button
          variant="outline"
          onClick={handleResetFilters}
          className="flex-1"
        >
          Reset
        </Button>
        <Button
          variant="default"
          onClick={handleApplyFilters}
          className="flex-1"
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );

  // Mobile Filter Button
  if (!isOpen) {
    return (
      <Button
        variant="outline"
        iconName="Filter"
        iconPosition="left"
        onClick={() => onFiltersChange(filters)}
        className="relative"
      >
        Filters
        {getActiveFilterCount() > 0 && (
          <span className="absolute -top-2 -right-2 bg-accent text-accent-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center">
            {getActiveFilterCount()}
          </span>
        )}
      </Button>
    );
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-80 bg-card border border-border rounded-lg p-6 h-fit">
        <FilterContent />
      </div>

      {/* Mobile Modal */}
      <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-sm animate-fade-in">
        <div className="fixed inset-y-0 right-0 w-full max-w-sm bg-card border-l border-border shadow-lg animate-slide-up">
          <div className="h-full overflow-y-auto p-6">
            <FilterContent />
          </div>
        </div>
      </div>
    </>
  );
};

export default ShopFilters;